import React from 'react';
import { WarehouseNode } from '../../types';
import './NodeManagement.css';

interface NodeListProps {
  nodes: WarehouseNode[];
  onEditNode: (node: WarehouseNode) => void;
  onDeleteNode: (nodeId: string) => void;
  loading?: boolean;
}

const NodeList: React.FC<NodeListProps> = ({ 
  nodes, 
  onEditNode, 
  onDeleteNode, 
  loading = false 
}) => {
  if (loading) {
    return (
      <div className="node-list-container">
        <div className="loading">Loading warehouse nodes...</div>
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="node-list-container">
        <div className="empty-state">
          <h3>No Warehouse Nodes</h3>
          <p>Get started by adding your first warehouse node to the network.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="node-list-container">
      <h3>Warehouse Network ({nodes.length} nodes)</h3>
      
      {/* Table View */}
      <div className="node-table-container">
        <table className="node-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Location</th>
              <th>Capacity</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {nodes.map((node) => (
              <tr key={node.node_id} className={!node.active ? 'inactive' : ''}>
                <td className="node-name">
                  <strong>{node.name}</strong>
                </td>
                <td>{node.location || 'Not specified'}</td>
                <td>
                  {node.capacity ? `${node.capacity.toLocaleString()} units` : 'Unlimited'}
                </td>
                <td>
                  <span className={`status-badge ${node.active ? 'active' : 'inactive'}`}>
                    {node.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  {new Date(node.created_at).toLocaleDateString()}
                </td>
                <td className="actions">
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={() => onEditNode(node)}
                    title="Edit node"
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-sm btn-danger"
                    onClick={() => onDeleteNode(node.node_id)}
                    title="Delete node"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Network Layout View */}
      <div className="network-layout">
        <h4>Network Layout</h4>
        <div className="network-grid">
          {nodes.map((node) => (
            <div 
              key={node.node_id} 
              className={`network-node ${!node.active ? 'inactive' : ''}`}
              onClick={() => onEditNode(node)}
              title={`${node.name} - ${node.location}`}
            >
              <div className="node-icon">
                <span className="warehouse-icon">🏭</span>
              </div>
              <div className="node-info">
                <div className="node-name">{node.name}</div>
                <div className="node-location">{node.location}</div>
                {node.capacity && (
                  <div className="node-capacity">
                    Cap: {node.capacity.toLocaleString()}
                  </div>
                )}
              </div>
              <div className={`node-status ${node.active ? 'active' : 'inactive'}`}>
                {node.active ? '●' : '○'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NodeList;